"use client";

import CheckIcon from "@/icons/__generated__/Check/Check16";
import { cn } from "@/lib/utils";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { cva, VariantProps } from "class-variance-authority";
import * as React from "react";
import { Label } from "../Label";

const checkboxVariants = cva(
  "tw-bg-base-white tw-inline-flex tw-items-center tw-justify-center tw-rounded-2 tw-border tw-border-gray-400 tw-size-16 tw-align-text-top tw-peer tw-shrink-0 tw-ring-offset-background focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-blue-800 focus-visible:tw-ring-offset-1 disabled:tw-pointer-events-none disabled:tw-bg-gray-300 data-[state=checked]:tw-bg-gray-900 data-[state=checked]:tw-border-gray-900 data-[state=checked]:tw-text-base-white",
  {
    variants: {
      alignment: {
        top: "tw-mt-4", // accounts for label line-height
        center: "",
      },
    },
  },
);

type CheckboxBoxWrapperProps = React.PropsWithChildren<{
  errorMsg?: string;
}>;
const CheckboxBoxWrapper = ({
  children,
  errorMsg,
}: CheckboxBoxWrapperProps) => {
  return (
    <div
      className={cn(
        "tw-p-24 tw-bg-gray-100 tw-border tw-border-gray-300 tw-rounded-4",
        {
          "tw-border-red-800": !!errorMsg,
        },
      )}
    >
      {children}
    </div>
  );
};

export type CheckboxProps = React.ComponentPropsWithoutRef<
  typeof CheckboxPrimitive.Root
> &
  VariantProps<typeof checkboxVariants> & {
    description?: React.ReactNode;
    testId?: string;
    errorMsg?: string;
    variant?: "default" | "box";
  };

const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  CheckboxProps
>(
  (
    {
      description,
      className,
      testId,
      alignment = "center",
      errorMsg,
      variant = "default",
      ...props
    },
    ref,
  ) => {
    const Wrapper = variant === "box" ? CheckboxBoxWrapper : React.Fragment;
    const wrapperProps = variant === "box" ? { errorMsg } : {};

    return (
      <Wrapper {...wrapperProps}>
        <div
          className={cn("tw-inline-flex tw-items-center tw-gap-8 tw-relative", {
            "tw-items-start": alignment === "top",
          })}
        >
          <CheckboxPrimitive.Root
            ref={ref}
            className={cn(checkboxVariants({ className, alignment }))}
            data-testid={testId}
            {...props}
          >
            <CheckboxPrimitive.Indicator
              className={cn(
                "tw-flex tw-items-center tw-justify-center tw-text-current",
              )}
            >
              <CheckIcon />
            </CheckboxPrimitive.Indicator>
          </CheckboxPrimitive.Root>

          {description && (
            <Label className="tw-body-2 tw-cursor-pointer" htmlFor={props.id}>
              {description}
            </Label>
          )}
        </div>
      </Wrapper>
    );
  },
);
Checkbox.displayName = CheckboxPrimitive.Root.displayName;

export { Checkbox };
